<template>
    <div class="container py-5">
        <h2>{{ $t('distributionCharts') }}</h2>
        <nav aria-label="breadcrumb" class="mb-4">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">{{ $t('home') }}</RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: $route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modeling/${$route.params.id}/modelingresult/${$route.params.modelingResultId}`">
                        {{ $t('modelingResult') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('distributionCharts') }}</li>
            </ol>
        </nav>
        <div v-if="pending" class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
        <div v-else>
            <div class="row mb-5" v-for="cfg in chartConfigs" :key="cfg.limitId">
                <h5 class="mb-3">{{ $t('limit') }}: {{ cfg.limitName }}</h5>
                <div class="col-md-8">
                    <div v-if="getSelectedChartTotalWeight(cfg)" class=" mb-1">
                        Total {{ getSelectedChartTotalWeight(cfg).weightAttributeName }}: {{ getSelectedChartTotalWeight(cfg).totalWeight }}
                    </div>
                    <apexchart
                        v-if="getSelectedChart(cfg)"
                        :key="chartKey(cfg)"
                        type="bar"
                        width="800"
                        :height="500"
                        :options="getChartOptions(getSelectedChart(cfg), cfg)"
                        :series="getSelectedChart(cfg).series"
                        class="d-flex justify-content-center"
                    />
                </div>
                <div class="col-md-4">
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('assetType') }}</label>
                        <select class="col-md-8 form-select" v-model.number="cfg.selectedAssetType" @change="assetTypeChanged(cfg)">
                            <option v-for="at in assetTypeList" :key="at.id" :value="at.id">
                                {{ at.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('dataValues') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedDataValue" @change="getGraphData(cfg)">
                            <option value="" disabled selected>Select</option>
                            <option v-for="dataValue in dataValuesList?.filter(p => p.assetTypeId == cfg.selectedAssetType)" :key="dataValue.value" :value="dataValue.value">
                                {{ dataValue.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('area') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedGroupId">
                            <option :value="null">{{ $t('allAreas') }}</option>
                            <option v-for="g in groupsList.filter(p => p.assetTypeId === cfg.selectedAssetType)" :key="g.value" :value="Number(g.value)">
                                {{ g.name }}
                            </option>
                        </select>
                    </div>
                    <div class="row mb-3">
                        <label class="col-md-4">{{ $t('package') }}</label>
                        <select class="col-md-8 form-select" v-model="cfg.selectedPackageId">
                            <option :value="null">{{ $t('allPackages') }}</option>
                            <option v-for="p in packagesList.filter(x => x.assetTypeId === cfg.selectedAssetType)" :key="p.value" :value="Number(p.value)" >
                                {{ p.name }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex mb-4">
                <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${$route.params.id}/modelingresult/${$route.params.modelingResultId}` })">
                {{ $t('back') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import AnalysisApi from "../../api/server/analysis";
import { CheckIfUserIsEditor } from "../../authCache";

export default {
    name: "distributionCharts",
    watch: {
        $route: "requestData"
    },
    async mounted() {
        await this.requestData();
    },
    data() {
        return {
            data: {},
            pending: true,
            isUserAdmin: false,

            // raw payload
            limits: [],
            assetTypeList: [],
            dataValuesList: [],
            chartConfigs: [],
            groupsList: [],
            packagesList: [],
            modelingName: ""
        };
    },
    methods: {
        getSelectedChartTotalWeight(cfg) {
            const chart = this.getSelectedChart(cfg);
            if (!chart || chart.dataValue !== 'performance')
                return null;
            if (!chart.totalWeight && !chart.weightAttributeName)
                return null;
            return {
                totalWeight: chart.totalWeight,
                weightAttributeName: chart.weightAttributeName
            };
        },
        async getGraphData(cfg) {
        },
        chartKey(cfg) {
            return `${cfg.limitId}-${cfg.selectedAssetType}-${cfg.selectedDataValue}-${cfg.selectedGroupId ?? 'all'}-${cfg.selectedPackageId ?? 'all'}`;
        },
        assetTypeChanged(cfg) {
            cfg.selectedDataValue = this.getSelectedDataValue(cfg.assetTypes?.[0]?.id, cfg.dataValues);
            cfg.selectedGroupId = null;
            cfg.selectedPackageId = null;
        },
        formatAmount(value) {
            return `${value}%`;
        },
        getSelectedChart(cfg) {
            const filteredCharts = cfg.charts.filter(
                c => c.assetTypeId == cfg.selectedAssetType && c.dataValue == cfg.selectedDataValue
            );
            if (!filteredCharts.length) return null;

            const gid = cfg.selectedGroupId;
            const pid = cfg.selectedPackageId;

            // ALL
            if (gid == null && pid == null) {
                return filteredCharts.find(c => c.groupId == null && c.packageId == null) || null;
            }

            // only group
            if (gid != null && pid == null) {
                return filteredCharts.find(c => c.groupId === gid && c.packageId == null) || null;
            }

            // only package
            if (gid == null && pid != null) {
                return filteredCharts.find(c => c.groupId == null && c.packageId === pid) || null;
            }

            // group + package
            return filteredCharts.find(c => c.groupId === gid && c.packageId === pid) || null;
        },
        getChartOptions(chart, cfg) {
            return {
                chart: {
                    type: 'bar',
                    stacked: true,
                    stackType: '100%',
                    height: 500,
                    animations: { enabled: false }
                },
                title: {
                    // text: this.$t('distributionTitle', { limit: cfg.limitName })
                },
                xaxis: {
                    categories: chart.categories || chart.years,
                    title: { text: this.$t('year') }
                },
                yaxis: {
                    title: { text: this.$t('percentage') },
                    labels: { formatter: v => this.formatAmount(v) }
                },
                tooltip: {
                    y: { formatter: v => this.formatAmount(v) }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '50%',
                        endingShape: 'rounded'
                    }
                },
                dataLabels: { enabled: false },
                legend: { position: 'top' },
                colors: chart.series?.map(s => s.color)
            };
        },
        getSelectedDataValue(assetTypeId, dataValues) {
            return dataValues.find(v => v.selected && v.assetTypeId == assetTypeId)?.value || "performance";
        },
        async requestData() {
            this.isUserAdmin = await CheckIfUserIsEditor();

            const analysisDetail = await AnalysisApi.analysisDetail(this.$route.params.id, this.$route.params.modelingResultId);
            
            this.modelingName = analysisDetail.modelingInfo?.name || '';

            const result = await AnalysisApi.distributionCharts(this.$route.params.id,this.$route.params.modelingResultId);

            this.assetTypeList = result.assetTypes;
            this.dataValuesList = result.dataValues;
            this.groupsList = result.groups;
            this.packagesList = result.packages;
            this.chartConfigs = result.limits.map(limitGroup => ({
                limitId: limitGroup.limitId,
                limitName: limitGroup.limitName,
                charts: limitGroup.charts,
                selectedAssetType: result.assetTypes?.[0]?.id || null,
                selectedDataValue: this.getSelectedDataValue(result.assetTypes?.[0]?.id, result.dataValues),
                selectedGroupId: null,
                selectedPackageId: null
            }));

            this.pending = false;
        }
    }
};
</script>
